import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { NAVIGATE_FROM_NOTIFICATION, type NotificationNavigateMessage } from "./swMessages";

/**
 * Receives the service worker's notification-click navigation intent and
 * performs the in-app route change.
 *
 * The worker focuses the tab and posts a {@link NotificationNavigateMessage}
 * rather than calling WindowClient.navigate() (which rejects on uncontrolled
 * clients and forces a full reload). This hook is the page-side half of that
 * contract: it owns a live navigator.serviceWorker message listener and drives
 * React Router with the basename-relative path the worker computed.
 *
 * Mount it once inside the authenticated, router-scoped shell (see
 * NotificationNavigationBridge) — it must have a router context for
 * useNavigate, and an unauthenticated tab has nowhere to deep-link to.
 */
export function useNotificationNavigation(): void {
    const navigate = useNavigate();

    useEffect(() => {
        if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
            return;
        }
        const onMessage = (event: MessageEvent) => {
            const data = event.data as NotificationNavigateMessage | null;
            if (data?.type !== NAVIGATE_FROM_NOTIFICATION) {
                return;
            }
            if (typeof data.path === "string" && data.path.length > 0) {
                void navigate(data.path);
            }
        };
        navigator.serviceWorker.addEventListener("message", onMessage);
        return () => navigator.serviceWorker.removeEventListener("message", onMessage);
    }, [navigate]);
}
